import {
  Box,
  Button,
  List,
  Modal,
  Typography,
  useDesktopMediaQuery,
} from '@iheartjane/reefer';
import { Storage } from '@iheartjane/utils';
import { memo, useEffect, useState } from 'react';

import CannabisBrochure from './CA_Brochure_BW.webp';

export const CA_BROCHURE_KEY = 'californiaBrochureSeen';

interface Props {
  cartUuid: string;
  state: string | null;
}

export const CaliforniaBrochure = memo(function CaliforniaBrochure({
  cartUuid,
  state,
}: Props) {
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });
  const sawBrochureCartUuid = Storage.get(CA_BROCHURE_KEY);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!sawBrochureCartUuid && state === 'California') {
      Storage.set(CA_BROCHURE_KEY, cartUuid);
    }
  }, [sawBrochureCartUuid, cartUuid]);

  // If the user is not in california, or has seen the brochure when
  // checking out previously (i.e. with a different cart UUID), don't show
  if (
    state !== 'California' ||
    (!!sawBrochureCartUuid && sawBrochureCartUuid !== cartUuid)
  )
    return null;

  const listItemStyle = {
    listStyleType: 'initial',
    padding: 0,
    '> div': { padding: 0 },
  };

  return (
    <Box my={16} mx={isDesktop ? 'auto' : 16} overflow="scroll">
      <Typography variant="h2-semibold" ml={12} my={16} width="100%">
        CA Cannabis Use Warning:
      </Typography>
      <img
        src={CannabisBrochure}
        alt="Effects of cannabis use"
        height="800px"
        width="auto"
      />
      <Button
        label="View brochure as text"
        onClick={() => setModalOpen(true)}
        variant="minimal"
      />
      <Modal open={modalOpen} onRequestClose={() => setModalOpen(false)}>
        <Modal.Header title="Cannabis Effects" textAlign="center" />
        <Modal.Content>
          <Box>
            <Typography variant="h2-semibold">High Potency</Typography>

            <Typography variant="body-semibold">
              Consuming higher-potency THC products does not mean a better
              cannabis experience.
            </Typography>

            <Typography>
              Higher-potency THC products are harder to control and come with a
              higher risk of adverse effects. Using them often may cause
              dependency in some people.
            </Typography>

            <Typography>
              If you are new to cannabis, be careful with higher-potency
              products like concentrates and vape cartridges.
            </Typography>

            <Typography>
              If you are buying cannabis, compare labels and consider
              lower-potency products:
            </Typography>

            <List style={{ paddingLeft: '16px' }} label="Low potency products">
              <List.Item style={listItemStyle}>
                <Typography>Flower with less than 20% THC</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  Edibles with close to 5mg THC per serving (or consume half of
                  a 10mg THC serving)
                </Typography>
              </List.Item>
            </List>

            <Typography>
              If you are inhaling cannabis, wait between puffs until you feel
              the full effect.
            </Typography>

            <Typography variant="h2-semibold" mt={24}>
              Pregnancy and Breastfeeding
            </Typography>

            <Typography variant="body-semibold">
              There are risks to your baby from using cannabis. Your baby
              considers what you do.
            </Typography>

            <Typography>Do not use any cannabis if you are:</Typography>
            <List style={{ paddingLeft: '16px' }} label="do not use if">
              <List.Item style={listItemStyle}>
                <Typography>pregnant or may be pregnant</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>breastfeeding</Typography>
              </List.Item>
            </List>

            <Typography>This includes:</Typography>
            <List style={{ paddingLeft: '16px' }} label="cannabis usages">
              <List.Item style={listItemStyle}>
                <Typography>
                  inhaling cannabis (smoking, vaping, dabbing)
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>eating or drinking cannabis products</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  applying cannabis-infused creams or lotions
                </Typography>
              </List.Item>
            </List>

            <Typography>
              No matter how you consume cannabis, THC and other chemicals will
              be passed to your baby through your placenta and breastmilk.
            </Typography>

            <Typography>
              If you use medicinal cannabis, ask your doctor about alternative
              treatments during pregnancy or while breastfeeding.
            </Typography>

            <Typography variant="h2-semibold" mt={24}>
              Effects of Cannabis Use
            </Typography>

            <Typography variant="body-semibold">
              Wondering what to expect when using cannabis?
            </Typography>

            <Typography>
              Cannabis has two major chemicals that affect the brain
              differently:
            </Typography>

            <List style={{ paddingLeft: '16px' }} label="cannabis chemicals">
              <List.Item style={listItemStyle}>
                <Typography>
                  <strong>THC (Tetrahydrocannabinol)</strong> makes you feel
                  "high". How you feel depends on how much THC you take, and the
                  effects can vary from person to person.
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  <strong>CBD (Cannabidiol)</strong> doesn't make you feel
                  "high", but can have other effects.
                </Typography>
              </List.Item>
            </List>

            <Typography variant="body-semibold">
              The effects of using cannabis may include:
            </Typography>
            <List
              style={{ paddingLeft: '16px' }}
              label="effects of cannabis use"
            >
              <List.Item style={listItemStyle}>
                <Typography>
                  Trouble with thinking, remembering, and problem-solving
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  Seeing or hearing things that aren't real (with high doses
                  and/or high potency)
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  Feeling like time is moving slower or faster
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Feeling relaxed or paranoid</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Having mood swings</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Having increased or decreased anxiety</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Feeling dizzy</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Having dry mouth or bloodshot eyes</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  Relieving pain and reducing nausea/vomiting
                </Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Impaired movement and coordination</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Feeling hungry</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>
                  Faster heartbeat or lower blood pressure
                </Typography>
              </List.Item>
            </List>

            <Typography variant="body-semibold">
              Edibles: Start Low, Go Slow.
            </Typography>

            <Typography variant="body-semibold">
              Use extra caution, as the delayed onset of edibles can have
              unintended effects.
            </Typography>
            <Typography>
              {' '}
              If you consume a cannabis edible (like brownies, gummies, or an
              infused beverage), it can take up to 2 hours to begin feeling the
              effects, and up to 4 hours to feel the full effects. This is much
              slower than smoking or vaping, and the "high" can feel stronger.
              Start with a small amount of THC (5mg or less) and wait at least 2
              hours to see how you feel.
            </Typography>

            <Typography variant="body-semibold">
              When consuming edibles, you can always take more later, but you
              can't take it back.
            </Typography>

            <Typography variant="body-semibold">
              Drive high, get a DUI.
            </Typography>

            <Typography>
              Driving under the influence of cannabis is illegal and increases
              your risk of getting into an accident. Penalties for driving high
              are the same as driving drunk.
            </Typography>

            <Typography variant="h2-semibold" mt={24}>
              Mental Health
            </Typography>

            <Typography variant="body-semibold">
              There are potential risks to your mental health from cannabis use.
            </Typography>

            <Typography>
              Research suggests that mental health disorders may develop or
              worsen from:
            </Typography>
            <List
              style={{ paddingLeft: '16px' }}
              label="health conditions causes"
            >
              <List.Item style={listItemStyle}>
                <Typography>Daily cannabis use</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Near-daily cannabis use</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Higher-potency cannabis use</Typography>
              </List.Item>
            </List>

            <Typography>
              These types of use have been linked to Cannabis Use Disorder, and
              may lead to:
            </Typography>
            <List
              style={{ paddingLeft: '16px' }}
              label="cannabis use disorder symptoms"
            >
              <List.Item style={listItemStyle}>
                <Typography>Hallucinations</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Schizophrenia</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Depression</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Anxiety</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Temporary psychosis</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Thoughts of self harm</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Suicide attempts</Typography>
              </List.Item>
              <List.Item style={listItemStyle}>
                <Typography>Suicide</Typography>
              </List.Item>
            </List>

            <Typography variant="h2-semibold" mt={24}>
              Minors and Young Adults
            </Typography>

            <Typography>
              Our brains actively develop until around the age of 25. Using
              cannabis regularly while younger can cause long-term problems with
              thinking and mental health.
            </Typography>

            <Typography>
              <strong>Parents, be aware.</strong> Edibles and other cannabis
              products can be mistaken for food or candy. Like medications, you
              should keep cannabis products in child-resistant packaging. Store
              and dispose of cannabis products where they cannot be easily seen
              or accessed by children and pets.
            </Typography>

            <Typography variant="h2-semibold" mt={24}>
              Contact Information:
            </Typography>
            <Typography>California Poison Control: (800) 222-1222</Typography>
            <Typography>Animal Poison Control: (888) 426-4435</Typography>
            <Typography>Dept of Cannabis Control: cannabis.ca.gov</Typography>
            <Typography>Dept of Public Health: cdph.ca.gov</Typography>
          </Box>
        </Modal.Content>
      </Modal>
    </Box>
  );
});
